import './ButtonSelect.scss'

import Dropdown, { closeDropdownEvent } from '../Dropdown/Dropdown'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import PropTypes from 'prop-types'
import classNames from 'classnames'

const ButtonSelect = ({
  name,
  label,
  initialLabel,
  size,
  disabled,
  onSelected,
  onAction,
  children
}) => {
  const [_label, setLabel] = useState(initialLabel)
  const buttonSelectRef = useRef()

  useLayoutEffect(() => {
    const eventListener = (event) => {
      if (event.detail.action === 'select') {
        setLabel(event.detail.label)
        if (onSelected) onSelected(event.detail.value, name)
      } else if (event.detail.action === 'action') {
        if (onAction) onAction(event.detail.value, name)
      }
    }
    const element = buttonSelectRef.current
    element.addEventListener('ButtonSelectEvent', eventListener, false)
    return () => {
      element.removeEventListener('ButtonSelectEvent', eventListener, false)
    }
  }, [onAction, name, onSelected])

  return (
    <div ref={buttonSelectRef}>
      <Dropdown
        button={
          <Button
            label={label || _label}
            icon='ArrowDownIcon'
            type='borderless'
            size={size}
            iconPosition='trailing'
            disabled={disabled}
          />
        }
        basic
        disabled={disabled}
      >
        {children}
      </Dropdown>
    </div>
  )
}

const ButtonSelectItem = ({
  value,
  label,
  icon,
  reportLabel,
  unselectable,
  selected
}) => {
  const [initialSelection] = useState(selected)
  const item = useRef()

  const handleClick = useCallback(() => {
    item.current.dispatchEvent(
      buttonSelectEvent({
        action: unselectable ? 'action' : 'select',
        value: value || reportLabel || label,
        label: reportLabel || label
      })
    )
    item.current.dispatchEvent(closeDropdownEvent())
  }, [value, label, reportLabel, unselectable])

  useEffect(() => {
    if (initialSelection) {
      item.current.dispatchEvent(
        buttonSelectEvent({
          action: 'select',
          value: value || reportLabel || label,
          label: reportLabel || label
        })
      )
    }
  }, [initialSelection, value, label, reportLabel])

  return (
    <div ref={item} className='button-select-item' onClick={handleClick}>
      <div className='-icon'>
        {unselectable ? (
          <Icon size={14} icon={icon} />
        ) : (
          selected && <Icon size={14} icon='ArrowIcon' />
        )}
      </div>
      <div
        className={classNames('-text', {
          'label-field-sized-basic': !selected,
          'label-field-sized-medium': selected
        })}
      >
        {label}
      </div>
    </div>
  )
}

const ButtonSelectSeparator = () => (
  <div className='button-select-separator'></div>
)

const buttonSelectEvent = ({ action, value, label }) => {
  return new CustomEvent('ButtonSelectEvent', {
    bubbles: true,
    cancelable: true,
    detail: {
      action,
      value,
      label
    }
  })
}

ButtonSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  initialLabel: PropTypes.string,
  size: PropTypes.oneOf(['small', 'field-sized', 'default', 'medium', 'large']),
  disabled: PropTypes.bool,
  onSelected: PropTypes.func,
  onAction: PropTypes.func
}

ButtonSelect.defaultProps = {
  size: 'default'
}

ButtonSelectItem.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  reportLabel: PropTypes.string,
  unselectable: PropTypes.bool,
  selected: PropTypes.bool
}

ButtonSelectItem.defaultProps = {}

export default ButtonSelect
export { ButtonSelectItem, ButtonSelectSeparator }
